/**
 * State that belongs to a selection, and resets when the selection changes.
 *
 * The obvious way to write this is an effect that clears the state when the
 * class or the date changes. That is wrong twice over: it cascades a second
 * render, and — the part that matters — for ONE render the old state is still
 * paired with the new selection. On the appel screen that render is yesterday's
 * marks sitting under today's date, one click away from being posted.
 *
 * Deriving it instead makes that impossible rather than unlikely: the marks
 * carry the scope they were made in, and a scope mismatch reads as empty.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useScopedState<T>(scope: string, initial: T) {
  const [held, setHeld] = useState<{ scope: string; value: T }>({ scope, value: initial });

  const value = held.scope === scope ? held.value : initial;

  // The setter must keep a STABLE identity across renders. It did not, and the
  // cost was a login screen that could never finish searching: SchoolSelect
  // lists these setters in an effect's dependency array, so a new identity on
  // every render re-ran the effect, whose cleanup aborted the in-flight
  // lookup — and the effect's own setState("searching") caused the render that
  // re-ran it. Every request came back net::ERR_ABORTED and the box said
  // "Recherche…" forever.
  //
  // scope and initial are read through a ref rather than closed over, so the
  // callback can be stable while still seeing the current scope. The ref is
  // written in an effect, not during render: a render must stay pure, and
  // under concurrent rendering a render that React throws away would
  // otherwise leave the ref pointing at a scope that never existed. Every
  // caller sets from an event handler or an effect, both of which run after
  // this one.
  const latest = useRef({ scope, initial });
  useEffect(() => {
    latest.current = { scope, initial };
  });

  const set = useCallback((next: T | ((current: T) => T)) => {
    const { scope: currentScope, initial: currentInitial } = latest.current;
    setHeld((current) => {
      const base = current.scope === currentScope ? current.value : currentInitial;
      return {
        scope: currentScope,
        value: typeof next === "function" ? (next as (current: T) => T)(base) : next,
      };
    });
  }, []);

  return [value, set] as const;
}
